import type { MailListRowDataType, TableView } from "owa-mail-list-store";
import { MailRowDataPropertyGetter, isItemOfMessageType, canTableBeOutOfSyncWithServer, isMailListConversationView } from "owa-mail-list-store";
import { type TombstoneReasonType } from "owa-mail-list-tombstone";
import type { ConversationType, ItemRow } from "owa-graph-schema";
import type Message from "owa-service/lib/contract/Message";
import arrayEquals from "array-equal";

/**
 * Property sync map that stores the tombstone reason type to sync functions
 */
const clientToServerPropertySyncMap = new Map<TombstoneReasonType, (serverRow: MailListRowDataType, tableView: TableView) => boolean>();

clientToServerPropertySyncMap.set(1, syncClientPinPropertyToServerRow);
clientToServerPropertySyncMap.set(0, syncClientCategoryPropertyToServerRow);
clientToServerPropertySyncMap.set(2, syncClientReadPropertyToServerRow);
clientToServerPropertySyncMap.set(5, syncClientFlagPropertyToServerRow);

// No/Op for Meeting removes. This is just a way to signal the LV to not load info for same meeting events in the same conversation
clientToServerPropertySyncMap.set(4, () => true);

/**
 * Syncs the client (local) PIN property by stamping it on the server row payload
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @returns flag indicating whether the pin operation property value was already in sync
 */
function syncClientPinPropertyToServerRow(serverRow: MailListRowDataType, tableView: TableView): boolean {
    let serverPinPropertyValue;
    const isConversationListViewType = isMailListConversationView(tableView);
    
    if (isConversationListViewType) {
        serverPinPropertyValue = (serverRow as ConversationType).LastDeliveryOrRenewTime;
    }
    else {
        serverPinPropertyValue = (serverRow as ItemRow).ReceivedOrRenewTime;
    }
    
    const clientPinPropertyValue = MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(serverRow.InstanceKey as string, tableView);
    
    const isPinPropertyInSync = 
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2769 (76,18): No overload matches this call.
    // @ts-expect-error
    new Date(serverPinPropertyValue).toString() === new Date(clientPinPropertyValue).toString();
    
    if (!isPinPropertyInSync) {
        if (isConversationListViewType) {
            (serverRow as ConversationType).LastDeliveryOrRenewTime = clientPinPropertyValue;
        }
        else {
            (serverRow as ItemRow).ReceivedOrRenewTime = clientPinPropertyValue;
        }
    }
    
    return isPinPropertyInSync;
}

/**
 * Syncs the client (local) ReceivedTime property by stamping it on the server row payload
 * only for conversation type rows. This is used for a special scenario to sync the time property
 * for update notifications happening in out-of-sync tables
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 */
function syncClientReceivedTimeToServerConversationRow(serverRow: MailListRowDataType, tableView: TableView) {
    const conversationRow = serverRow as ConversationType;
    const serverReceivedTime = conversationRow.LastDeliveryTime;
    const clientReceivedTime = MailRowDataPropertyGetter.getLastDeliveryTimeStamp(serverRow.InstanceKey as string, tableView);
    
    const isReceivedTimeInSync = new Date(serverReceivedTime).toString() === new Date(clientReceivedTime).toString();
    
    if (!isReceivedTimeInSync) {
        conversationRow.LastDeliveryTime = clientReceivedTime;
    }
}

/**
 * Syncs the client (local) CATEGORY property by stamping it on the server row payload
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @returns flag indicating whether the category property value was already in sync
 */
function syncClientCategoryPropertyToServerRow(serverRow: MailListRowDataType, tableView: TableView): boolean {
    const clientCategories = MailRowDataPropertyGetter.getCategories(serverRow.InstanceKey as string, tableView);
    const clientCategoriesSorted = clientCategories ? clientCategories.slice().sort() : [];
    const serverCategoriesSorted = serverRow.Categories ? serverRow.Categories.slice().sort() : [];
    const isCategoryPropertyInSync = arrayEquals(clientCategoriesSorted, serverCategoriesSorted);
    
    if (!isCategoryPropertyInSync) {
        serverRow.Categories = clientCategories;
    }
    
    return isCategoryPropertyInSync;
}

/**
 * Syncs the client (local) READ property by stamping it on the server row payload
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @returns flag indicating whether the read property value was already in sync
 */
function syncClientReadPropertyToServerRow(serverRow: MailListRowDataType, tableView: TableView): boolean {
    let payloadRow;
    let payloadUnreadCount;
    let isReadPropertyInSync;
    
    const clientUnreadCount = MailRowDataPropertyGetter.getUnreadCount(serverRow.InstanceKey as string, tableView);
    
    const isConversationListViewType = isMailListConversationView(tableView);
    
    if (isConversationListViewType) {
        payloadRow = serverRow as ConversationType;
        payloadUnreadCount = payloadRow.UnreadCount;
        isReadPropertyInSync = clientUnreadCount == payloadUnreadCount;
        if (!isReadPropertyInSync) {
            payloadRow.UnreadCount = clientUnreadCount;
        }
        
        /**
         * If this is a table that can be out of sync with the server
         * (currently only the unread filter table can be out of sync when server setting
         * for keep read item in unread view is enabled) we could get update notifications
         * for large conversations as unread count is gradually decreasing. In this case
         * only syncing the Read value is not enough as the row can move in the view
         * as the new local timestamp property will be that of the latest unread item in the conversation
         * which is changing with each notification. So we would also sync the time based properties
         * that matter for the sorting. For non date sorted tables, this is still required as other wise
         * the timestamps shall flip which also can cause flickers.
         */
        if (canTableBeOutOfSyncWithServer(tableView)) {
            syncClientReceivedTimeToServerConversationRow(serverRow, tableView);
            syncClientPinPropertyToServerRow(serverRow, tableView);
        }
    }
    else {
        // For non-message types we shall not sync anything
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (198,33): Argument of type 'MailListRowDataType' is not assignable to parameter of type 'Item'.
        // @ts-expect-error
        if (isItemOfMessageType(serverRow)) {
            payloadRow = serverRow as Message;
            payloadUnreadCount = payloadRow.IsRead ? 0 : 1;
            isReadPropertyInSync = clientUnreadCount == payloadUnreadCount;
            
            if (!isReadPropertyInSync) {
                payloadRow.IsRead = clientUnreadCount == 0;
            }
        }
        else {
            isReadPropertyInSync = true;
        }
    }
    
    return isReadPropertyInSync;
}

/**
 * Syncs the client (local) FLAG properties (FlagStatus and FlagDueDate) by stamping
 * them on the server row payload. Defends against stale RowModified notifications
 * that arrive without the user's just-applied flag changes.
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @returns flag indicating whether the flag property values were already in sync
 */
function syncClientFlagPropertyToServerRow(serverRow: MailListRowDataType, tableView: TableView): boolean {
    const isConversationListViewType = isMailListConversationView(tableView);
    
    const clientFlagStatus = MailRowDataPropertyGetter.getFlagStatus(serverRow.InstanceKey as string, tableView);
    const clientFlagDueDate = MailRowDataPropertyGetter.getFlagDueDate(serverRow.InstanceKey as string, tableView);
    
    let serverFlagStatus;
    let serverFlagDueDate;
    if (isConversationListViewType) {
        const conversationRow = serverRow as ConversationType;
        serverFlagStatus = conversationRow.FlagStatus;
        serverFlagDueDate = conversationRow.ConversationFlagDueDate ?? null;
    }
    else {
        const itemRow = serverRow as ItemRow;
        serverFlagStatus = itemRow.Flag?.FlagStatus;
        serverFlagDueDate = itemRow.Flag?.DueDate ?? null;
    }
    
    const normalizedClientStatus = clientFlagStatus ?? "NotFlagged";
    const normalizedServerStatus = serverFlagStatus ?? "NotFlagged";
    const isFlagPropertyInSync = normalizedClientStatus === normalizedServerStatus &&
        (clientFlagDueDate ?? null) === (serverFlagDueDate ?? null);
    
    if (!isFlagPropertyInSync) {
        if (isConversationListViewType) {
            const conversationRow = serverRow as ConversationType;
            conversationRow.FlagStatus = clientFlagStatus;
            conversationRow.ConversationFlagDueDate = clientFlagDueDate;
        }
        else {
            const itemRow = serverRow as ItemRow;
            itemRow.Flag = {
                ...(itemRow.Flag ?? {}),
                FlagStatus: clientFlagStatus,
                DueDate: clientFlagDueDate,
            };
        }
    }
    
    return isFlagPropertyInSync;
}

/**
 * Syncs client triage property for the given triage operation in the server row payload
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @param tombstoneReason type of tombstone reason used to determine which property to sync
 * @returns flag indicating whether the triage property for given tombstone reason is in sync
 */
export default function syncClientPropertyToServerRow(serverRow: MailListRowDataType, tableView: TableView, tombstoneReason: TombstoneReasonType): boolean {
    const syncFunctionForTombstoneReason = clientToServerPropertySyncMap.get(tombstoneReason);
    if (!syncFunctionForTombstoneReason) {
        return true;
    }
    return syncFunctionForTombstoneReason(serverRow, tableView);
}
